import { useCallback, useEffect, useRef } from 'react'
import { useFt } from 'hooks'
import logger from 'logger'
import cookieStorage from 'cookie-storage'
import { track } from 'analytics'
import { useHistory } from 'router'
import { CancelledPromiseError, CausedError, constants, PaymentTokenError, processError } from 'helpers'
import { openNotification } from 'notifications'
import { useApolloClient } from 'apollo-client'
import links, { getLinkWithParams } from 'links'

import {
  useAfterPaymentUpdate,
  useInquireProvider,
  usePaymentFlowProvider,
  useProcessAnalytics,
  usePurchase,
} from 'modules/payment'
import { useCart } from 'modules/cart'
import { useShippingForm, useUser } from 'modules/user'
import { useSubscription } from 'modules/subscription'
import type { PurchaseLevel } from 'typings/graphql'


const DAY_IN_SECONDS = 24 * 60 * 60
const DAY_IN_MILLISECONDS = DAY_IN_SECONDS * 1000

const useCartPaymentFlow = ({ purchaseLevel }: { purchaseLevel: PurchaseLevel}) => {
  const { user } = useUser()

  const shippingForm = useShippingForm({ initialValues: user?.addressInfo?.shipping, useLocalStorage: false })
  const billingForm = useShippingForm({ initialValues: user?.addressInfo?.billing, useLocalStorage: false })
  const hasShippingAddressRef = useRef(Boolean(user?.addressInfo?.shipping?.id))

  const purchase = usePurchase()
  const history = useHistory()
  const { cart } = useCart()
  // inquire
  const inquire = useInquireProvider({
    purchaseLevel,
    shippingForm,
    billingForm,
  })

  const { submit: submitInquire, waitForData: waitForInquire } = inquire

  const isGiftSubscriptionOfferEnabled = useFt(constants.features.giftSubscriptionOffer)
  const { subscription } = useSubscription()
  const isSubscriptionCancelled = subscription?.isCancelled

  // update inquire if cart total has changed
  useEffect(() => {
    void (async () => {
      await submitInquire()
    })()
  }, [ submitInquire, cart?.total ])

  // analytics
  const processAnalytics = useProcessAnalytics()
  const updateSubscriptions = useAfterPaymentUpdate()
  const apolloClient = useApolloClient()

  const redirectWithCacheReset = useCallback((to: string) => {
    // reset cart cache to re-fetch these items in future
    apolloClient.cache.evict({ id: 'UserData:{}', fieldName: 'cart' })
    apolloClient.cache.evict({ id: 'UserData:{}', fieldName: 'queue' })
    apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'academyCourseList' })
    apolloClient.cache.gc()

    track('Checkout redirect', {
      link: to,
    })

    history.push(to)
  }, [ history, apolloClient ])

  const handleSubmit = useCallback<PaymentModule.PaymentFlowOnSubmit>(async ({ tokenGetter, paymentMethod }) => {
    try {
      track('Checkout start', {
        paymentMethod,
      })

      logger.info('[Apple pay in cart] start')

      if (!tokenGetter || !paymentMethod) {
        openNotification('serverError')
        logger.error(new Error('[Apple pay in cart] no tokenGetter or payment method'))
        return
      }

      const { tokenId } = await tokenGetter()

      logger.info('[Apple pay in cart] token id')

      const orderData = await waitForInquire()
      const { hasSubscription, hasGiftSubscription, giftSubscriptionOfferCount, isGiftsOnly } = orderData

      const shippingAddress = shippingForm.getValues()
      const billingAddress = billingForm.getValues()

      const purchaseResult = await purchase({
        purchaseLevel: 'ALL',
        paymentMethod,
        tokenId,
        shipping: isGiftsOnly ? null : shippingAddress,
        billing: billingAddress,
      })

      track('Place order button click', {
        isSuccessful: true,
        paymentMethod: paymentMethod,
        isExpressCheckout: !paymentMethod,
        inCart: true,
      })

      processAnalytics({ purchaseResult })

      if (hasSubscription) {
        try {
          await updateSubscriptions({
            enableAddons: hasSubscription,
            withSubscription: hasSubscription,
            withAddressInfo: !hasShippingAddressRef.current,
          })
        }
        catch (error) {
          logger.error(error)
        }
      }

      if (isGiftSubscriptionOfferEnabled && hasGiftSubscription && !hasSubscription && isSubscriptionCancelled && giftSubscriptionOfferCount) {
        cookieStorage.setItem(constants.cookieNames.giftSubscriptionOffer, giftSubscriptionOfferCount, { maxAge: DAY_IN_SECONDS })
        cookieStorage.setItem(constants.cookieNames.giftSubscriptionOfferCountdown, DAY_IN_MILLISECONDS, { maxAge: DAY_IN_SECONDS })

        redirectWithCacheReset(`${links.subscription.main}?notification=gift-subscription-resubscribe`)
      }
      else {
        const thankYouPageBaseLink = getLinkWithParams(links.thankYou, { sessionId: purchaseResult.session.id })
        const skipUpgradeStep = orderData.realPurchaseLevel === 'SUBSCRIPTION_ONLY' && orderData.items.subscription.plan.name !== 'MONTHLY'

        redirectWithCacheReset(`${thankYouPageBaseLink}${skipUpgradeStep ? '?skipUpgrade=true' : ''}`)
      }
    }
    catch (error) {
      let reason: string
      let reasonType: Analytics.Events['Place order button click']['reasonType']

      if (error instanceof CancelledPromiseError) {
        reasonType = 'Express checkout stopped'
        reason = 'Cancelled'
      }
      else if (error instanceof CausedError && error.cause) {
        const cause = error.cause

        if (error instanceof PaymentTokenError) {
          reasonType = 'Invalid payment method'
        }
        else if (cause.__typename) {
          reasonType = 'Site BE error'
        }

        reason = cause?.message || JSON.stringify(cause)
      }
      else {
        reason = error.message || JSON.stringify(error)
      }

      track('Place order button click', {
        isSuccessful: false,
        isExpressCheckout: false,
        reason,
        reasonType,
        inCart: true,
        paymentMethod,
      })

      if (error?.cause) {
        const cause = error.cause

        // if order is already processed we redirect user to the main page
        if (
          cause.purchaseErrorCode === 'ALREADY_SUBSCRIBED'
          || cause.purchaseBeginErrorCode === 'CART_IS_EMPTY'
          || cause.purchaseBeginErrorCode === 'ALREADY_SUBSCRIBED'
          || cause.purchaseCommitErrorCode === 'ALREADY_PROCESSED'
        ) {
          logger.warn(cause, 'Payment failed because of a duplicated status')
          return
        }
      }

      processError(error, '[Apple pay in cart] stop because of an error')
    }
  }, [
    shippingForm,
    billingForm,
    waitForInquire,
    processAnalytics,
    purchase,
    redirectWithCacheReset,
    updateSubscriptions,
    isGiftSubscriptionOfferEnabled,
    isSubscriptionCancelled,
  ])

  const cartPaymentFlow = usePaymentFlowProvider({
    flow: 'PAYMENT',
    onSubmit: handleSubmit,
    shippingForm,
    billingForm,
  })

  return {
    cartPaymentFlow,
    inquire,
  }
}

export default useCartPaymentFlow
